import * as THREE from 'three';
import { Input } from './core/Input.js';
import { Audio } from './core/Audio.js';
import { Player } from './entities/Player.js';
import { Weapon } from './entities/Weapon.js';
import { ProjectilePool } from './entities/Projectile.js';
import { EnemyManager } from './entities/Enemy.js';
import { Arena } from './world/Arena.js';
import { WaveManager } from './systems/WaveManager.js';
import { Combat } from './systems/Combat.js';
import { Particles } from './systems/Particles.js';
import { HUD } from './ui/HUD.js';
import { Screens } from './ui/Screens.js';

const STATES = {
  LOADING: 'loading',
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAME_OVER: 'game_over',
};

export class Game {
  constructor() {
    this.state = STATES.LOADING;
    this.clock = new THREE.Clock();
    this.renderer = null;
    this.scene = null;
    this.camera = null;

    // Systems
    this.input = new Input();
    this.audio = new Audio();
    this.hud = new HUD();
    this.screens = new Screens();

    // Game objects (initialized in init)
    this.player = null;
    this.weapon = null;
    this.projectiles = null;
    this.enemies = null;
    this.arena = null;
    this.waveManager = null;
    this.combat = null;
    this.particles = null;
  }

  init() {
    // Renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    document.body.insertBefore(this.renderer.domElement, document.body.firstChild);

    // Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x000011, 0.008);

    // Camera
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 500);
    this.scene.add(this.camera);

    // Game objects
    this.player = new Player(this.camera);
    this.weapon = new Weapon(this.scene, this.camera);
    this.weapon._audio = this.audio;
    this.weapon.attachToCamera(this.camera);
    this.projectiles = new ProjectilePool(this.scene);
    this.enemies = new EnemyManager(this.scene);
    this.arena = new Arena(this.scene);
    this.player.coverObjects = this.arena.coverObjects;
    this.waveManager = new WaveManager(this.enemies);
    this.combat = new Combat();
    this.particles = new Particles(this.scene);

    // Screen callbacks
    this.screens.init();
    this.screens.onPlay = () => this._startGame();
    this.screens.onResume = () => this._resumeGame();
    this.screens.onQuit = () => this._quitToMenu();
    this.screens.onRestart = () => this._startGame();
    this.screens.onMenu = () => this._quitToMenu();

    // Window resize
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });

    // Pointer lock change handling – only pause when using keyboard+mouse.
    // Gamepad players don't need pointer lock, so losing it doesn't pause.
    document.addEventListener('pointerlockchange', () => {
      if (!document.pointerLockElement && this.state === STATES.PLAYING && !this.input.gamepadConnected) {
        this._pauseGame();
      }
    });

    // "Loading" - we have no external assets, so just fake a brief load
    this.screens.showLoading();
    let progress = 0;
    const loadInterval = setInterval(() => {
      progress += 15;
      this.screens.setLoadingProgress(Math.min(100, progress));
      if (progress >= 100) {
        clearInterval(loadInterval);
        setTimeout(() => {
          this.state = STATES.MENU;
          this.screens.showMenu();
        }, 300);
      }
    }, 80);

    // Start game loop
    this._loop();
  }

  _startGame() {
    this.audio.init();
    this.audio.resume();
    this.audio.playClick();

    // Reset everything
    this.player.reset();
    this.weapon.reset();
    this.projectiles.reset();
    this.enemies.reset();
    this.waveManager.reset();
    this.combat.reset();
    this.particles.reset();
    this.hud.displayedScore = 0;

    this.screens.hideAll();
    this.hud.show();

    // Request pointer lock
    this.input.requestLock(this.renderer.domElement);
    this.state = STATES.PLAYING;

    this.audio.startMusic(1);

    // Start first wave
    this.clock.getDelta(); // reset delta
    this._startNextWave();
  }

  _pauseGame() {
    if (this.state !== STATES.PLAYING) return;
    this.state = STATES.PAUSED;
    this.screens.showPause();
    this.audio.stopMusic();
  }

  _resumeGame() {
    this.audio.playClick();
    this.screens.hidePause();
    this.input.requestLock(this.renderer.domElement);
    this.input.consumeMouse(); // discard look-input accumulated while paused
    this.state = STATES.PLAYING;
    this.audio.startMusic(this.waveManager.wave);
    this.clock.getDelta(); // reset delta to avoid jump
  }

  _quitToMenu() {
    this.audio.playClick();
    this.audio.stopMusic();
    this.state = STATES.MENU;
    this.input.exitLock();
    this.hud.hide();
    this.screens.showMenu();
    this.enemies.reset();
    this.projectiles.reset();
    this.particles.reset();
  }

  _gameOver() {
    this.state = STATES.GAME_OVER;
    this.input.exitLock();
    this.hud.hide();
    this.audio.stopMusic();
    this.audio.playGameOver();

    this.screens.showGameOver({
      score: this.combat.score,
      wave: this.waveManager.wave,
      kills: this.combat.totalKills,
      accuracy: this.weapon.accuracy,
      highestCombo: this.combat.highestCombo,
    });
  }

  _startNextWave() {
    const wave = this.waveManager.startNextWave();
    this.hud.showWaveAnnouncement(wave, this.waveManager.getWaveSubtext());
    if (wave > 1) {
      this.audio.playWaveComplete();
      this.audio.stopMusic();
      this.audio.startMusic(wave);
    }
  }

  _loop() {
    requestAnimationFrame(() => this._loop());

    const dt = Math.min(this.clock.getDelta(), 0.05); // cap delta time

    // Poll gamepad every frame so buttons work in menus, not just during gameplay
    this.input.pollGamepad(dt);

    if (this.state === STATES.PLAYING) {
      this._updateGameplay(dt);
    } else if (this.state === STATES.MENU) {
      // A button → start game
      if (this.input.consumeJump()) this._startGame();
    } else if (this.state === STATES.PAUSED) {
      // A button or Start button → resume
      if (this.input.consumeJump() || this.input.consumeEsc()) this._resumeGame();
    } else if (this.state === STATES.GAME_OVER) {
      // A button → restart
      if (this.input.consumeJump()) this._startGame();
    }

    // Always render (for menu backgrounds etc)
    this.renderer.render(this.scene, this.camera);
  }

  _updateGameplay(dt) {
    // Input handling
    if (this.input.consumeEsc()) {
      this._pauseGame();
      return;
    }

    if (this.input.consumeReload()) {
      this.weapon.startReload(this.audio);
    }

    // Player update
    this.player.update(dt, this.input);

    // Weapon update
    this.weapon.update(dt);

    // Shooting
    if (this.input.isFiring && this.weapon.canFire()) {
      const shotInfo = this.weapon.fire();
      if (shotInfo) {
        this.projectiles.spawn(
          shotInfo.origin,
          shotInfo.direction,
          shotInfo.speed,
          shotInfo.damage,
          true,
        );
        this.audio.playShoot();
        this.hud.addScreenShake(0.15);
        this.particles.spawnMuzzleFlash(shotInfo.origin.clone().add(shotInfo.direction.clone().multiplyScalar(1)), shotInfo.direction);
      }
    }

    // Update projectiles
    this.projectiles.update(dt);

    // Update enemies
    this.enemies.update(dt, this.player.position, this.projectiles, this.audio);

    // Combat checks
    const hits = this.combat.checkPlayerProjectiles(
      this.projectiles.getActive(),
      this.enemies.getActive(),
      this.weapon,
      this.audio,
      this.particles,
      this.camera,
    );

    for (const hit of hits) {
      this.hud.showHitMarker(hit.killed);
      if (hit.killed) {
        this.hud.addScreenShake(0.3);
        this.waveManager.onEnemyKilled();
        this.particles.spawnScorePopup(hit.position, hit.points);
      }
    }

    // Enemy projectile vs obstacles (blocks shots; must run before vs-player check)
    this.combat.checkObstacleProjectiles(
      this.projectiles.getActive(),
      this.arena.coverObjects,
      this.particles,
    );

    // Enemy projectile vs player
    const playerHit = this.combat.checkEnemyProjectiles(
      this.projectiles.getActive(),
      this.player,
    );
    if (playerHit) {
      this.audio.playDamage();
      this.hud.showDamageVignette();
      this.hud.addScreenShake(0.2);

      if (!this.player.alive) {
        this._gameOver();
        return;
      }
    }

    // Enemy body collision vs player
    const collisionHit = this.combat.checkEnemyCollisions(
      this.enemies.getActive(),
      this.player,
    );
    if (collisionHit) {
      this.audio.playDamage();
      this.hud.showDamageVignette();
      this.hud.addScreenShake(0.3);

      if (!this.player.alive) {
        this._gameOver();
        return;
      }
    }

    // Combat system update (combos etc)
    this.combat.update(dt);

    // Wave manager
    this.waveManager.update(dt);
    if (this.waveManager.isIdle && this.player.alive) {
      this._startNextWave();
    }

    // Particles
    this.particles.update(dt);

    // Arena
    this.arena.update(dt);

    // Screen shake
    const shake = this.hud.getScreenShakeOffset();
    this.camera.position.x += shake.x;
    this.camera.position.y += shake.y;

    // HUD
    this.hud.update(dt, {
      health: this.player.health,
      maxHealth: this.player.maxHealth,
      shield: this.player.shield,
      maxShield: this.player.maxShield,
      ammo: this.weapon.ammo,
      reloading: this.weapon.reloading,
      reloadProgress: this.weapon.reloading ? 1 - (this.weapon.reloadTimer / this.weapon.reloadTime) : 0,
      score: this.combat.score,
      wave: this.waveManager.wave,
      combo: this.combat.combo,
      comboMultiplier: this.combat.comboMultiplier,
      enemyCount: this.enemies.count,
    });
  }
}
